import { AggregateSourceTypes } from 'config/consts';
import { useCallback, useState } from 'react';
import {
  getAccountGroup,
  GetAccountGroupResult,
  getUserBroker,
  GetUserBrokerResult,
  operateAccountGroup,
  OperateAccountGroupParam,
} from 'config/api';
import { getAggregateSourceName } from 'components/features/utils/converter';

type Tagged<S extends symbol, T> = T & {
  tag: S;
};

const UNCATEGORIZED_ID = Symbol('UNCATEGORIZED_ID');
export type UncategorizedId = Tagged<typeof UNCATEGORIZED_ID, { id: -1 }>;
export const UncategorizedGroup: { id: UncategorizedId; name: '未分類' } = {
  id: { id: -1, tag: UNCATEGORIZED_ID },
  name: '未分類',
} as const;
const UNNAMED_ID = Symbol('UNNAMED_ID');
export type UnnamedId = Tagged<typeof UNNAMED_ID, { id: string }>;
export const unnamedId = (id: string): UnnamedId => ({
  id,
  tag: UNNAMED_ID,
});
export type UnnamedGroup = {
  id: UnnamedId;
  name: '';
};
export const isUnnamedGroup = (state: Group): state is UnnamedGroup => {
  return state.id.tag === UNNAMED_ID;
};
const CATEGORIZED_ID = Symbol('CATEGORIZED_ID');
type CategorizedId = Tagged<typeof CATEGORIZED_ID, { id: string }>;
const UNDECIDED_ID = Symbol('UNDECIDED_ID');
export type UndecidedId = Tagged<typeof UNDECIDED_ID, { id: string }>;
export const undecidedId = (num: number): UndecidedId => ({
  tag: UNDECIDED_ID,
  id: `newId${num}`,
});
export const isUndecidedId = (
  obj: CategorizedId | UndecidedId
): obj is UndecidedId => {
  return obj.tag && obj.tag === UNDECIDED_ID;
};
export const isUncategorizedId = (obj: GroupId): obj is UncategorizedId => {
  return obj.tag && obj.tag === UNCATEGORIZED_ID;
};
export const TARGET_GOAL_REF_KEY = {
  老後資金: 1,
  FIRE: 2,
  教育資金: 3,
  海外留学: 4,
  住宅頭金: 5,
  リフォーム: 6,
  引越資金: 7,
  自分の店: 8,
  起業する: 9,
  結婚資金: 10,
  海外挙式: 11,
  新婚旅行: 12,
  旅行資金: 13,
  世界一周: 14,
  新車資金: 15,
  投資資金: 16,
  その他: 17,
} as const;
const isTargetGoalRefKey = (
  num: number
): num is typeof TARGET_GOAL_REF_KEY[keyof typeof TARGET_GOAL_REF_KEY] => {
  return (
    num === 1 ||
    num === 2 ||
    num === 3 ||
    num === 4 ||
    num === 5 ||
    num === 6 ||
    num === 7 ||
    num === 8 ||
    num === 9 ||
    num === 10 ||
    num === 11 ||
    num === 12 ||
    num === 13 ||
    num === 14 ||
    num === 15 ||
    num === 16 ||
    num === 17
  );
};
export const TARGET_GOAL_REF: {
  [key in typeof TARGET_GOAL_REF_KEY[keyof typeof TARGET_GOAL_REF_KEY]]: {
    type: string;
    image: string;
  };
} = {
  [TARGET_GOAL_REF_KEY.老後資金]: {
    type: '老後資金',
    image: 'icon-goal1.svg',
  },
  [TARGET_GOAL_REF_KEY.FIRE]: { type: 'FIRE', image: 'icon-goal2.svg' },
  [TARGET_GOAL_REF_KEY.教育資金]: {
    type: '教育資金',
    image: 'icon-goal3.svg',
  },
  [TARGET_GOAL_REF_KEY.海外留学]: {
    type: '海外留学',
    image: 'icon-goal4.svg',
  },
  [TARGET_GOAL_REF_KEY.住宅頭金]: {
    type: '住宅頭金',
    image: 'icon-goal5.svg',
  },
  [TARGET_GOAL_REF_KEY.リフォーム]: {
    type: 'リフォーム',
    image: 'icon-goal6.svg',
  },
  [TARGET_GOAL_REF_KEY.引越資金]: {
    type: '引越資金',
    image: 'icon-goal7.svg',
  },
  [TARGET_GOAL_REF_KEY.自分の店]: {
    type: '自分の店',
    image: 'icon-goal8.svg',
  },
  [TARGET_GOAL_REF_KEY.起業する]: {
    type: '起業する',
    image: 'icon-goal9.svg',
  },
  [TARGET_GOAL_REF_KEY.結婚資金]: {
    type: '結婚資金',
    image: 'icon-goal10.svg',
  },
  [TARGET_GOAL_REF_KEY.海外挙式]: {
    type: '海外挙式',
    image: 'icon-goal11.svg',
  },
  [TARGET_GOAL_REF_KEY.新婚旅行]: {
    type: '新婚旅行',
    image: 'icon-goal12.svg',
  },
  [TARGET_GOAL_REF_KEY.旅行資金]: {
    type: '旅行資金',
    image: 'icon-goal13.svg',
  },
  [TARGET_GOAL_REF_KEY.世界一周]: {
    type: '世界一周',
    image: 'icon-goal14.svg',
  },
  [TARGET_GOAL_REF_KEY.新車資金]: {
    type: '新車資金',
    image: 'icon-goal15.svg',
  },
  [TARGET_GOAL_REF_KEY.投資資金]: {
    type: '投資資金',
    image: 'icon-goal16.svg',
  },
  [TARGET_GOAL_REF_KEY.その他]: {
    type: 'その他',
    image: 'icon-goal17.svg',
  },
} as const;
export const getIconSrcFromRefKey = (
  refKey: typeof TARGET_GOAL_REF_KEY[keyof typeof TARGET_GOAL_REF_KEY]
): string => {
  return `/static/images/advisor/${TARGET_GOAL_REF[refKey].image}`;
};
type TargetGoalRef = typeof TARGET_GOAL_REF[typeof TARGET_GOAL_REF_KEY[keyof typeof TARGET_GOAL_REF_KEY]];
export type TargetGoal = {
  id: string;
  title: string;
  ref: TargetGoalRef;
};
export type CategorizedGroup = {
  id: CategorizedId | UndecidedId;
  tabNum: number;
  name: string;
  createdAt: Date;
  targetGoal: TargetGoal | null;
};
export const isCategorized = (group: Group): group is CategorizedGroup => {
  return group.id.tag === CATEGORIZED_ID || group.id.tag === UNDECIDED_ID;
};
export type GroupId = CategorizedId | UncategorizedId | UndecidedId | UnnamedId;
export type Group = typeof UncategorizedGroup | CategorizedGroup | UnnamedGroup;
export type AssetId = string;
export const AGGREGATE_SOURCE = {
  [getAggregateSourceName(
    AggregateSourceTypes.テコテック
  )]: AggregateSourceTypes.テコテック,
  [getAggregateSourceName(
    AggregateSourceTypes.MONO
  )]: AggregateSourceTypes.MONO,
  [getAggregateSourceName(
    AggregateSourceTypes.マネーツリー
  )]: AggregateSourceTypes.マネーツリー,
  [getAggregateSourceName(
    AggregateSourceTypes.手入力
  )]: AggregateSourceTypes.手入力,
} as const;
export type AggregateSource = typeof AGGREGATE_SOURCE[keyof typeof AGGREGATE_SOURCE];
export type Asset = {
  id: AssetId;
  index: number;
  groupId: GroupId;
  brokerName: string;
  totalAssetAmount: number | '---';
  aggregateSource: AggregateSource;
  isTallyTarget: boolean;
  refresh: { isOutOfDate: boolean; account: string } | null;
};

const UNLOADED = Symbol('UNLOADED');
const LOADING = Symbol('LOADING');
const LOADED = Symbol('LOADED');
type Unloaded = Tagged<
  typeof UNLOADED,
  {
    group: {
      groups: Group[];
    };
    changingState: [];
  }
>;
type Loading = Tagged<
  typeof LOADING,
  {
    group: {
      groups: Group[];
    };
    changingState: [];
  }
>;
const LOCAL_CHANGING = Symbol('LOCAL_CHANGING');
const API_CALLING = Symbol('API_CALLING');
type LocalChanging = Tagged<
  typeof LOCAL_CHANGING,
  {
    time: number;
  }
>;
const localChanging = (time: number): LocalChanging => ({
  tag: LOCAL_CHANGING,
  time: time,
});
type APICalling = Tagged<
  typeof API_CALLING,
  {
    time: number;
  }
>;
type ChangingState = LocalChanging | APICalling;
type LoadedState = {
  group: {
    groups: Group[];
  };
  assets: Asset[];
  changingState: ChangingState[];
};

type Loaded = Tagged<typeof LOADED, LoadedState>;
export const isUnloaded = (state: Grouping): state is Unloaded => {
  return 'tag' in state && state.tag === UNLOADED;
};
export const isLoading = (state: Grouping): state is Loading => {
  return 'tag' in state && state.tag === LOADING;
};
export const isLoaded = (state: Grouping): state is Loaded => {
  return 'tag' in state && state.tag === LOADED;
};
const unloaded: Unloaded = {
  tag: UNLOADED,
  group: { groups: [] },
  changingState: [],
};
const loading: Loading = {
  tag: LOADING,
  group: { groups: [] },
  changingState: [],
};
export const loaded = (state: LoadedState): Loaded => {
  return {
    tag: LOADED,
    ...state,
  };
};
export type Grouping = Unloaded | Loading | Loaded;
type CallGetGroupingAPI = () => Promise<void>;
type CallSetGrouping = (
  updater: (prevState: Grouping) => Grouping
) => Promise<void>;

const shouldCallAPI = (
  groupingState: Grouping,
  prevGroupingState: Grouping | undefined
): boolean => {
  // 以前と比べて変更があるかを検出
  if (isLoading(groupingState) || isUnloaded(groupingState)) return false;
  if (!prevGroupingState) return false;
  if (isLoading(prevGroupingState) || isUnloaded(prevGroupingState))
    return false;
  const prevGroupNames = prevGroupingState.group.groups.map(
    group => group.name
  );
  const groupNames = groupingState.group.groups.map(group => group.name);
  const groupPrevInNew =
    prevGroupNames.filter(name => !groupNames.includes(name)).length === 0;
  const groupNewInPrev =
    groupNames.filter(name => !prevGroupNames.includes(name)).length === 0;
  const prevAssetGroupIds = prevGroupingState.assets.map(
    asset => asset.groupId
  );
  const assetGroupIds = groupingState.assets.map(asset => asset.groupId);
  const assetPrevInNew =
    prevAssetGroupIds.filter(groupId => !assetGroupIds.includes(groupId))
      .length === 0;
  const assetNewInPrev =
    assetGroupIds.filter(groupId => !prevAssetGroupIds.includes(groupId))
      .length === 0;
  const newEqualPrev =
    groupPrevInNew && groupNewInPrev && assetPrevInNew && assetNewInPrev;
  return !newEqualPrev;
};

export const useGrouping = (): [
  Grouping,
  {
    getGroupingAPI: CallGetGroupingAPI;
    setGrouping: CallSetGrouping;
  }
] => {
  const [groupingState, setGroupingState] = useState<Grouping>(unloaded);

  const callGetGroupingAPI = useCallback(async (): Promise<void> => {
    setGroupingState(loading);
    const [apiResult, userBrokers] = await Promise.all([
      getAPI(),
      getUserBroker(),
    ]);
    const convertedData = convertStateFrom(apiResult, userBrokers);
    setGroupingState(loaded(convertedData));
  }, []);

  const callSetGrouping = useCallback(
    async (newStateUpdater: (state: Grouping) => Grouping): Promise<void> => {
      // FIXME: setState内でresolveするのはバッドノウハウだったような気がすごいするが、何かしらの記述も見当たらず
      const now = new Date().getTime();
      const promise = new Promise<{
        newState: Grouping;
        shouldCallAPI: boolean;
      }>(r => {
        setGroupingState(prev => {
          const newState = newStateUpdater(prev);
          r({
            newState: newState,
            shouldCallAPI: shouldCallAPI(newState, prev),
          });
          if (isUnloaded(newState) || isLoading(newState)) return newState;
          return {
            ...newState,
            changingState: [...newState.changingState, localChanging(now)],
          };
        });
      });
      const state = await promise;

      if (isUnloaded(state.newState) || isLoading(state.newState)) return;
      if (state.shouldCallAPI) {
        // changingStateのlocalChangingになっているものをAPICallingに変更
        setGroupingState(prev => {
          if (isUnloaded(prev) || isLoading(prev)) return prev;
          return {
            ...prev,
            changingState: prev.changingState.map(cs => {
              if (cs.tag === LOCAL_CHANGING && cs.time === now)
                return { tag: API_CALLING, time: now };
              return cs;
            }),
          };
        });
        await setAPI(state.newState);
        // changingStateの現在のものを削除
        setGroupingState(prev => {
          if (isUnloaded(prev) || isLoading(prev)) return prev;
          return {
            ...prev,
            changingState: prev.changingState.filter(cs => cs.time !== now),
          };
        });
      } else {
        // APIを呼ばない変更だった場合、ずっとlocalChangingが溜まっていってしまうので削除
        setGroupingState(prev => {
          if (isUnloaded(prev) || isLoading(prev)) return prev;
          return {
            ...prev,
            changingState: prev.changingState.filter(cs => cs.time !== now),
          };
        });
      }
    },
    []
  );

  return [
    groupingState,
    {
      getGroupingAPI: callGetGroupingAPI,
      setGrouping: callSetGrouping,
    },
  ];
};

const getAPI = async (): Promise<GetAccountGroupResult> => {
  return await getAccountGroup();
};

const setAPI = async (loaded: Loaded): Promise<boolean> => {
  const dedupedIds = Array.from(new Set(loaded.assets.map(asset => asset.id)));
  // 重複する口座idがあるならapiを叩かない :(
  if (dedupedIds.length !== loaded.assets.length) return false;
  return (await operateAccountGroup(convertStateTo(loaded))).Success;
};

const convertStateFrom = (
  result: GetAccountGroupResult,
  userBrokers: GetUserBrokerResult
): Loaded => {
  const indexedUserBrokers = {
    Items: userBrokers.Items.map((item, index) => ({
      ...item,
      index: index,
    })),
  };
  const allAccountIds = indexedUserBrokers.Items.map(item => {
    return item.Account;
  });
  // groupが、存在しないaccountを持っていたらfiltering
  const validAccountFilteredGroups = result.Items.map(item => {
    return {
      ...item,
      Accounts: item.Accounts.filter(account =>
        allAccountIds.includes(account)
      ),
    };
  });
  const getGroupIdFromAccount = (account: string): string | null => {
    for (const as of validAccountFilteredGroups.map(item => {
      return {
        groupId: item.ID,
        accounts: item.Accounts.map(item => item),
      };
    })) {
      if (as.accounts.includes(account)) {
        return as.groupId;
      }
    }
    return null;
  };
  const uncategorizedAssets = indexedUserBrokers.Items.map(
    (account, index) => ({
      ...account,
      index: index,
    })
  ).filter(item => {
    return !getGroupIdFromAccount(item.Account);
  });

  const assets: Asset[] = [
    ...uncategorizedAssets.map(
      (item): Asset => {
        return {
          id: item.Account,
          index: item.index,
          groupId: { id: -1, tag: UNCATEGORIZED_ID },
          brokerName: item.BrokerName,
          totalAssetAmount:
            item.TotalAssetAmount !== null ? item.TotalAssetAmount : '---',
          aggregateSource: item.AggregateSource as AggregateSource,
          isTallyTarget: item.Available,
          refresh: !['000', null].includes(item.Status)
            ? { isOutOfDate: true, account: item.Account }
            : null,
        };
      }
    ),
    ...validAccountFilteredGroups
      .flatMap(g => {
        return g.Accounts.map(a => {
          return {
            groupId: g.ID,
            account: a,
          };
        });
      })
      .map(
        (item): Asset => {
          const userBrokerItem = indexedUserBrokers.Items.filter(
            v => v.Account === item.account
          );
          return {
            id: item.account,
            index: userBrokerItem[0].index,
            groupId: { id: item.groupId, tag: CATEGORIZED_ID },
            brokerName: userBrokerItem[0].BrokerName,
            totalAssetAmount:
              userBrokerItem[0].TotalAssetAmount !== null
                ? userBrokerItem[0].TotalAssetAmount
                : '---',
            aggregateSource: userBrokerItem[0]
              .AggregateSource as AggregateSource,
            isTallyTarget: userBrokerItem[0].Available,
            refresh: !['000', null].includes(userBrokerItem[0].Status)
              ? { isOutOfDate: true, account: userBrokerItem[0].Account }
              : null,
          };
        }
      ),
  ].sort((a, b) => a.index - b.index);

  const m = new Map();
  assets.forEach(asset => m.set(asset.id, asset));
  const dedupedAssets = Array.from(m.values());

  const categorizedGroups: CategorizedGroup[] = validAccountFilteredGroups.map(
    (g): CategorizedGroup => {
      const targetGoal: TargetGoal | null =
        g.TargetGoalID &&
        g.TargetGoalRefType &&
        isTargetGoalRefKey(g.TargetGoalRefType) &&
        g.TargetGoalTitle
          ? {
              id: g.TargetGoalID,
              title: g.TargetGoalTitle,
              ref: TARGET_GOAL_REF[g.TargetGoalRefType],
            }
          : null;
      return {
        id: { tag: CATEGORIZED_ID, id: g.ID },
        tabNum: Number(g.ID),
        name: g.Name,
        createdAt: new Date(g.InsTime),
        targetGoal: targetGoal,
      };
    }
  );

  return loaded({
    group: {
      groups: [UncategorizedGroup, ...categorizedGroups],
    },
    assets: dedupedAssets,
    changingState: [],
  });
};

type RegisterAPIArg = OperateAccountGroupParam;

export const getCategorizedGroups = (groups: Group[]): CategorizedGroup[] => {
  return groups.filter(group => isCategorized(group)) as CategorizedGroup[];
};

const convertStateTo = (loaded: Loaded): RegisterAPIArg => {
  const { assets, group } = loaded;
  const { groups } = group;
  return {
    Items: getCategorizedGroups(groups).map(group => {
      const ret = {
        Name: group.name,
        Accounts: assets
          .filter(asset => asset.groupId.id === group.id.id)
          .map(asset => asset.id),
        ID: isUndecidedId(group.id) ? group.tabNum.toString() : group.id.id,
      };
      return ret;
    }),
  };
};

export const getGroup = (
  assetId: string,
  groupingState: Grouping
): Group | null => {
  if (isLoaded(groupingState) && groupingState.assets) {
    const groupId = groupingState.assets.filter(
      asset => asset.id === assetId
    )[0].groupId;
    const group = groupingState.group.groups.filter(
      group => group.id.id === groupId.id
    )[0];
    return group;
  }
  return null;
};
